import { ReactElement, Suspense } from 'react'
import {
  Box,
  Text,
  Hidden,
  Heading,
  Breadcrumb,
  Button,
  Alert,
  TextLink
} from '@machobear/design-system'
import { useTranslation, Trans } from 'react-i18next'
import { ErrorBoundary } from 'react-error-boundary'
import { QueryErrorResetBoundary } from '@tanstack/react-query'
import useGetAutoReplyTriggers from '@/hooks/use-get-auto-reply-triggers'
import config from '@/config'
import TriggerList from './sub-components/TriggerList'
import AutoReplyPageWrapper from './sub-components/AutoReplyPageWrapper'
import { getFilteredData } from './helpers/utils'
import LoadingAutoReply from './sub-components/LoadingAutoReply'
const { WEB_URL } = config
const AutoReplyDashboard = () => {
  const { t } = useTranslation()
  const { data } = useGetAutoReplyTriggers()
  const hasAllowedUsage = data?.some((x: { allowed_usage: string }) => Number(x.allowed_usage) > 0)

  return (
    <AutoReplyPageWrapper title={t('l.settings.view.auto_reply.label', 'Auto-Reply')}>
      <Box flexDirection="column" display="flex" gap="small">
        <Box marginBottom={'xxsmall'}>
          <Breadcrumb
            variant="line"
            items={[
              {
                label: t('b.settings.view.settings.title', 'Settings'),
                href: '/settings'
              },
              {
                label: t('l.settings.view.auto_reply.label', 'Auto-Reply')
              }
            ]}
          />
        </Box>

        <Hidden below="tablet">
          <Heading level="4">{t('l.settings.view.auto_reply.label', 'Auto-Reply')}</Heading>
        </Hidden>
        <Text tone="secondary" baseline={false} size="small">
          {t(
            'l.settings.view.auto_reply.text',
            'Set up automated responses to save time responding to messages and send them automatically.'
          )}
        </Text>
        {!hasAllowedUsage && (
          <Alert tone="promote">
            <Trans t={t} i18nKey="l.settings.view.auto_reply.free_autoreply_used.text">
              <Text>
                You have used your free Auto-Reply.{' '}
                <TextLink href={`${WEB_URL}/pricing`} target="_blank">
                  Upgrade to our Growth plan
                </TextLink>{' '}
                for unlimited access.
              </Text>
            </Trans>
          </Alert>
        )}
      </Box>
      <ErrorBoundarySuspenseComponent>
        <TriggersComponent />
      </ErrorBoundarySuspenseComponent>
    </AutoReplyPageWrapper>
  )
}

export const TriggersComponent = () => {
  const { data } = useGetAutoReplyTriggers()

  const { t } = useTranslation()

  return (
    <>
      <TriggerList
        title={t('l.settings.view.auto_reply.message_received', 'Message Received')}
        data={getFilteredData(data, 'Message Received')}
      />
      <TriggerList
        title={t('l.settings.view.auto_reply.tenant_interaction', 'Tenant Interaction')}
        data={getFilteredData(data, 'Tenant Interaction')}
      />
      <TriggerList
        title={t('l.settings.view.auto_reply.listing_management', 'Listing Management')}
        data={getFilteredData(data, 'Listing Management')}
      />
    </>
  )
}

type ErrorBoundarySuspenseType = {
  children: ReactElement
}

export const ErrorBoundarySuspenseComponent: React.FC<ErrorBoundarySuspenseType> = ({
  children
}) => {
  const { t } = useTranslation()

  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ErrorBoundary
          onReset={reset}
          fallbackRender={({ resetErrorBoundary }) => (
            <Box>
              {t('common.there_was_an_error', 'There was an error!')}
              <Button size="small" variant={'ghost'} onClick={() => resetErrorBoundary()}>
                {t('common.try_again', 'Try Again')}
              </Button>
            </Box>
          )}
        >
          <Suspense fallback={<LoadingAutoReply />}>{children}</Suspense>
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  )
}

export default AutoReplyDashboard
